/*
 * <heritage-account-reserves>
 *
 * Dashboard preview of the partner's pending Reserve requests — the N
 * most recent. Reads from window.MDJReserves and re-renders on
 * `mdj:reserves-changed`. Falls back to the server-rendered
 * [data-empty] block when the partner has no reserves yet.
 *
 * Each card is a link to the PDP plus a small inline "Cancel" button
 * that calls MDJReserves.remove(id) — which fires the reserve.cancel
 * webhook + contact-form email (handled inside the store).
 *
 * Markup contract:
 *   <heritage-account-reserves data-limit="3">
 *     <div data-grid hidden></div>
 *     <div data-empty>(server-rendered empty state)</div>
 *   </heritage-account-reserves>
 */

class HeritageAccountReserves extends HTMLElement {
  constructor() {
    super();
    this.onChange = this.onChange.bind(this);
    this.handleCancelClick = this.handleCancelClick.bind(this);
  }

  connectedCallback() {
    this.limit = parseInt(this.getAttribute('data-limit') || '3', 10) || 3;
    this.gridEl = this.querySelector('[data-grid]');
    this.emptyEl = this.querySelector('[data-empty]');
    this.render();
    document.addEventListener('mdj:reserves-changed', this.onChange);
    this.addEventListener('click', this.handleCancelClick);
  }

  disconnectedCallback() {
    document.removeEventListener('mdj:reserves-changed', this.onChange);
    this.removeEventListener('click', this.handleCancelClick);
  }

  onChange() {
    this.render();
  }

  handleCancelClick(event) {
    const btn = event.target.closest && event.target.closest('[data-reserve-cancel]');
    if (!btn) return;
    event.preventDefault();
    const id = btn.dataset.reserveId;
    if (!id || !window.MDJReserves) return;
    if (!confirm('Cancel this reserve request? MDJ will be notified.')) return;
    const context = this.gatherContext();
    window.MDJReserves.remove(id, context);
  }

  gatherContext() {
    const cfg = window.__MDJ_INTEGRATIONS__ || {};
    const customer = cfg.customer || {};
    const addr = customer.default_address || {};
    return {
      customer_id: customer.id || null,
      email:       customer.email || '',
      first_name:  customer.first_name || '',
      last_name:   customer.last_name || '',
      phone:       addr.phone || customer.phone || '',
      company:     addr.company || '',
      tags:        customer.tags || '',
      cancelled_at: new Date().toISOString(),
      cancelled_at_local: new Date().toLocaleString()
    };
  }

  render() {
    if (!window.MDJReserves || !this.gridEl) return;
    const items = window.MDJReserves.getAll().slice(0, this.limit);

    if (items.length === 0) {
      this.gridEl.hidden = true;
      if (this.emptyEl) this.emptyEl.hidden = false;
      return;
    }

    if (this.emptyEl) this.emptyEl.hidden = true;
    this.gridEl.hidden = false;
    this.gridEl.innerHTML = items.map((item) => this.renderCard(item)).join('');
  }

  renderCard(item) {
    const safe = (s) => String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');

    const url = safe(item.url || ('/products/' + (item.handle || '')));
    const img = safe(item.image || '');
    const title = safe(item.title || '');
    const era = safe(item.era || '');
    // Strip the configured display prefix from the stored SKU.
    // Idempotent — safely no-ops on already-stripped entries (covers
    // legacy stored values from before the Liquid-side strip).
    const rawSku = item.sku || '';
    const sku = safe(window.MDJFormatSku ? window.MDJFormatSku(rawSku) : rawSku);
    const id = safe(String(item.id || ''));
    const status = item.status === 'unavailable' ? 'Unavailable' : 'Pending';
    const requested = item.requestedAt ? new Date(item.requestedAt).toLocaleDateString() : '';

    // Reserve intent — added so MDJ can distinguish memo-intent vs
    // purchase-intent reserves. Stored entries from before this field
    // landed don't carry an intent_label; the badge just doesn't render
    // for those (gracefully degraded). Visual style mirrors the era
    // eyebrow so it sits beside the status/date line without competing
    // with the title.
    const intentLabel = safe(item.intent_label || '');
    const intentBadge = intentLabel
      ? '<span class="heritage-account__list-card-intent" data-intent="' + safe(item.intent || '') + '">' + intentLabel + '</span>'
      : '';

    return (
      '<div class="heritage-account__list-card" data-reserve-card>' +
        '<a class="heritage-account__list-card-link" href="' + url + '" aria-label="' + title + '">' +
          '<span class="heritage-account__list-card-img"' + (img ? ' style="background-image: url(\'' + img + '\')"' : '') + '></span>' +
          '<span class="heritage-account__list-card-body">' +
            (era ? '<span class="heritage-account__list-card-era">' + era + '</span>' : '') +
            '<span class="heritage-account__list-card-name">' + title + '</span>' +
            (sku ? '<span class="heritage-account__list-card-sku">№ ' + sku + '</span>' : '') +
            '<span class="heritage-account__list-card-meta">' + status + (requested ? ' · ' + requested : '') + intentBadge + '</span>' +
          '</span>' +
        '</a>' +
        '<button type="button" class="heritage-account__list-card-cancel" data-reserve-cancel data-reserve-id="' + id + '" aria-label="Cancel reserve request">Cancel</button>' +
      '</div>'
    );
  }
}

if (!customElements.get('heritage-account-reserves')) {
  customElements.define('heritage-account-reserves', HeritageAccountReserves);
}
